import {
  useMemo,
  useState,
  useRef,
  useEffect,
  Fragment,
  forwardRef,
} from "react";
import { useParams, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import paycardImg from "./../images/cards/card.png";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import { Container } from "@mui/system";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import { useNavigate } from "react-router-dom";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { hasValue } from "../util/string";
import FmdBadIcon from "@mui/icons-material/FmdBad";
import Labels from "../labels/Labels";

// This key was created specifically for the demo in mui.com.
// You need to create a new one for your application.

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

export default function AssignPaycard({ onTheFly = false }) {
  const navigate = useNavigate();
  const { bundleDetailId } = useParams();
  const location = useLocation();
  const productType = new URLSearchParams(location.search).get("productType");
  const assignPaycardMutation = useMutation(
    (newPaycard) => {
      // Remove debugger statement
      if (hideSsn) {
        newPaycard.ssn = ssn;
      }
      newPaycard.ssn = newPaycard.ssn.replace(/-/g, "");
      newPaycard.ssn = newPaycard.ssn.replace(/ /g, "");
      if (bundleDetailId !== null && bundleDetailId !== undefined) {
        newPaycard.cardIdentifier = bundleDetailId;
      }
      if (hideMemberCode) {
        if (memberCode !== "") {
          newPaycard.employeeNo = memberCode;
        } else {
          newPaycard.employeeNo = newPaycard.mobilePhone;
        }
      }
      if (hidePhone) {
        if (mobilePhone !== "") {
          newPaycard.mobilePhone = mobilePhone;
        }
      }
      if (hideDateOfBirth) {
        if (dateOfBirth !== "") {
          newPaycard.dob = dateOfBirth;
        }
      }
      if (newPaycard.dob) {
        newPaycard.dob = new Date(newPaycard.dob).toISOString().split("T")[0];
      } else {
        delete newPaycard.dob;
      }

      if (useLastFourOfSSN && newPaycard.ssn) {
        newPaycard.ssn = newPaycard.ssn.padStart(9, "0");
      }

      if (noEmailChecked) {
        newPaycard.email = "noemail";
      }

      if (onTheFly) {
        newPaycard.onTheFly = true;
        if (productType) {
          newPaycard.cardType = productType;
        }
      }

      return HttpService.getAxiosClient().post(
        window.API_URL + `/paycards`,
        newPaycard
      );
    },
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "assignPaycard.CardAssignedSuccess",
            "card assigned succesfully"
          ),
          onClose: () => navigate("/"),
        });
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err?.response?.data
            ? err?.response?.data
            : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  async function fetchPaycardDetail() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/paycards/preassign/` +
        (onTheFly ? "onTheFly" : bundleDetailId)
    );
    setupFormWithServerData(response);
    return response;
  }

  const {
    data: dataPaycardDetail,
    isError: isErrorPaycardDetail,
    isFetching: isFetchingPayCardDetail,
  } = useQuery({
    queryKey: ["paycardDetail"],
    queryFn: fetchPaycardDetail,
    refetchOnWindowFocus: false,
  });

  async function fetchPaycardAsReplacementAttribute() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/attributes/PAYCARD_AS_REPLACEMENT`
    );
    return response;
  }

  async function fetchPreauthorizedMember(memberId) {
    if (!memberId || memberId.trim() === "") {
      return null;
    }
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/member/preauthorized/${memberId}`
    );
    return response;
  }

  const {
    data: dataPayCardAsReplacement,
    isFetching: isFetchingDataPayCardAsReplacement,
    isError: isErrorDataPayCardAsReplacement,
    //refetch: refetchDataPayCardAsReplacement
  } = useQuery(
    ["dataPayCardAsReplacement"],
    () => fetchPaycardAsReplacementAttribute(),
    {
      keepPreviousData: true,
    }
  );

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const handleLanguageChange = (event, idLanguage) => {
    setSelectedLanguage(idLanguage.props.value);
    formik.values.idLanguage = idLanguage.props.value;
  };

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // to setup visible fields
  const [ssn, setSsn] = useState("");
  const [memberCode, setMemberCode] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");

  const [hideSsn, setHideSsn] = useState(false);
  const [hideMemberCode, setHideMemberCode] = useState(false);
  const [hideAddress, setHideAddress] = useState(false);
  const [hidePhone, setHidePhone] = useState(false);
  const [hideDateOfBirth, setHideDateOfBirth] = useState(false);
  const [optionalSsn, setOptionalSsn] = useState(false);
  const [optionalMemberCode, setOptionalMemberCode] = useState(false);
  const [optionalPhone, setOptionalPhone] = useState(false);
  const [optionalDateOfBirth, setOptionalDateOfBirth] = useState(false);
  const [useLastFourOfSSN, setUseLastFourOfSSN] = useState(false);
  const [allowNoEmailCheck, setAllowNoEmailCheck] = useState(false);
  const [hideEmail, setHideEmail] = useState(false);
  const [hideNames, setHideNames] = useState(false);
  const [askForDenomination, setAskForDenomination] = useState(false);
  const [allowAdditionalAccountManager, setAllowAdditionalAccountManager] =
    useState(null);
  const [hideLanguage, setHideLanguage] = useState(false);

  const isEnabled = (value) => String(value).toLowerCase() === "true";
  const isValidPhoneNumber = (value) => {
    if (!value) {
      return false;
    }

    const trimmedValue = value.trim();
    if (trimmedValue.startsWith("+")) {
      // E.164-style international numbers: + followed by 8 to 15 digits.
      return /^\+[1-9]\d{7,14}$/.test(trimmedValue);
    }

    // Existing US validation behavior.
    return /^\(?[2-9]\d{2}\)?[-. ]?\d{3}[-. ]?\d{4}$/.test(trimmedValue);
  };

  // State for preauthorized member lookup
  const [memberIdToLookup, setMemberIdToLookup] = useState("");

  const US_STATES = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
    "DC", //District of Columbia, not a state but often included in lists of states
  ];

  const [validationSchemaObject, setValidationSchemaObject] = useState({
    firstName: yup
      .string(
        Labels.getLabel(
          "assignPaycard.validation.enterFirstName",
          "Enter the member First name"
        )
      )
      .matches(
        /^\S*$/,
        Labels.getLabel(
          "assignPaycard.validation.firstNameNoSpaces",
          "First name should not contain spaces"
        )
      )
      .required(
        Labels.getLabel(
          "assignPaycard.validation.firstNameRequired",
          "First name is required"
        )
      ),
    middle: yup.string(
      Labels.getLabel(
        "assignPaycard.validation.enterMiddle",
        "Enter the member middle"
      )
    ),
    lastName: yup
      .string(
        Labels.getLabel(
          "assignPaycard.validation.enterLastName",
          "Enter the member Last name"
        )
      )
      .matches(
        /^\S*$/,
        Labels.getLabel(
          "assignPaycard.validation.lastNameNoSpaces",
          "Last name should not contain spaces"
        )
      )
      .required(
        Labels.getLabel(
          "assignPaycard.validation.lastNameRequired",
          "Last name is required"
        )
      ),
    idLanguage: yup
      .string(
        Labels.getLabel(
          "assignPaycard.validation.selectLanguage",
          "Select the prefered language"
        )
      )
      .required(
        Labels.getLabel(
          "assignPaycard.validation.languageRequired",
          "Prefered Language is required"
        )
      ),
    addressLine1: yup
      .string(
        Labels.getLabel(
          "assignPaycard.validation.enterAddressLine1",
          "Enter the member Address Line 1"
        )
      )
      .required(
        Labels.getLabel(
          "assignPaycard.validation.addressLine1Required",
          "Address line 1 is required"
        )
      ),
    addressLine2: yup.string(
      Labels.getLabel(
        "assignPaycard.validation.enterAddressLine2",
        "Enter the member Address Line 2"
      )
    ),
    city: yup
      .string(
        Labels.getLabel(
          "assignPaycard.validation.enterCity",
          "Enter the member city"
        )
      )
      .required(
        Labels.getLabel(
          "assignPaycard.validation.cityRequired",
          "City is required"
        )
      ),
    state: yup
      .string(
        Labels.getLabel(
          "assignPaycard.validation.enterState",
          "Enter the member state"
        )
      )
      .length(
        2,
        Labels.getLabel(
          "assignPaycard.validation.stateLength",
          "State should be exactly 2 characters"
        )
      )
      .oneOf(
        US_STATES,
        Labels.getLabel(
          "assignPaycard.validation.stateNotValid",
          "State is not valid"
        )
      )
      .required(
        Labels.getLabel(
          "assignPaycard.validation.stateRequired",
          "State is required"
        )
      ),
    zipCode: yup
      .string(
        Labels.getLabel(
          "assignPaycard.validation.enterZipCode",
          "Enter the member zip code"
        )
      )
      .required(
        Labels.getLabel(
          "assignPaycard.validation.zipRequired",
          "zip is required"
        )
      ),
    amount: yup.number().when("askForDenomination", {
      is: true, // condition
      then: yup
        .number()
        .required(
          Labels.getLabel(
            "assignPaycard.validation.amountRequired",
            "Amount is required"
          )
        )
        .min(
          0,
          Labels.getLabel(
            "assignPaycard.validation.amountNonNegative",
            "Amount cannot be negative"
          )
        )
        .min(
          1,
          Labels.getLabel(
            "assignPaycard.validation.amountMin",
            "Min amount is 1"
          )
        )
        .max(
          1000,
          Labels.getLabel(
            "assignPaycard.validation.amountMax",
            "Max amount is 1000"
          )
        ),
      otherwise: yup.number().notRequired(),
    }),
  });

  const [validationSchema, setValidationSchema] = useState(
    yup.object(validationSchemaObject)
  );

  const [noEmailChecked, setNoEmailChecked] = useState(false);
  const handleNoEmailCheckChange = (event, checked) => {
    setNoEmailChecked(checked);

    let finalValidationSchema = { ...validationSchemaObject };

    if (!checked) {
      const emailValidation = yup
        .string(
          Labels.getLabel(
            "assignPaycard.validation.enterEmail",
            "Enter the member email"
          )
        )
        .email(
          Labels.getLabel(
            "assignPaycard.validation.validEmail",
            "Enter a valid email"
          )
        )
        .required(
          Labels.getLabel(
            "assignPaycard.validation.emailRequired",
            "Email is required"
          )
        );
      finalValidationSchema = {
        ...finalValidationSchema,
        email: emailValidation,
      };
    }

    if (!onTheFly) {
      finalValidationSchema = {
        ...finalValidationSchema,
        cardNumber: yup
          .string()
          .matches(
            /^[0-9][0-9][0-9][0-9]$/,
            Labels.getLabel(
              "assignPaycard.validation.cardNumberDigits",
              "Must be the last 4 digits of the card"
            )
          )
          .required(
            Labels.getLabel(
              "assignPaycard.validation.cardNumberRequired",
              "Card 4 last digits are required"
            )
          ),
      };
    }
    setValidationSchema(yup.object(finalValidationSchema));
  };

  const [additionalAccountManagerChecked, setAdditionalAccountManagerChecked] =
    useState(false);
  const handleAdditionalAccountManagerCheckChange = (event, checked) => {
    setAdditionalAccountManagerChecked(checked);

    let finalValidationSchema = { ...validationSchemaObject };

    if (checked) {
      const additionalAccountManagerFirstNameValidation = yup
        .string(
          Labels.getLabel(
            "assignPaycard.validation.enterAdditionalAccountManagerFirstName",
            "Enter the additional account manager first name"
          )
        )
        .required(
          Labels.getLabel(
            "assignPaycard.validation.additionalAccountManagerFirstNameRequired",
            "Additional account manager first name is required"
          )
        );
      const additionalAccountManagerLastNameValidation = yup
        .string(
          Labels.getLabel(
            "assignPaycard.validation.enterAdditionalAccountManagerLastName",
            "Enter the additional account manager last name"
          )
        )
        .required(
          Labels.getLabel(
            "assignPaycard.validation.additionalAccountManagerLastNameRequired",
            "Additional account manager last name is required"
          )
        );
      const addittionalAccountManagerDOBValidation = yup
        .date(
          Labels.getLabel(
            "assignPaycard.validation.enterAdditionalAccountManagerDOB",
            "Enter the additional account manager date of birth"
          )
        )
        .required(
          Labels.getLabel(
            "assignPaycard.validation.additionalAccountManagerDOBRequired",
            "Additional account manager date of birth is required"
          )
        );

      finalValidationSchema = {
        ...finalValidationSchema,
        additionalAccountManagerFirstName:
          additionalAccountManagerFirstNameValidation,
        additionalAccountManagerLastName:
          additionalAccountManagerLastNameValidation,
        additionalAccountManagerDob: addittionalAccountManagerDOBValidation,
      };
    }

    if (!onTheFly) {
      finalValidationSchema = {
        ...finalValidationSchema,
        cardNumber: yup
          .string()
          .matches(
            /^[0-9][0-9][0-9][0-9]$/,
            Labels.getLabel(
              "assignPaycard.validation.cardNumberDigits",
              "Must be the last 4 digits of the card"
            )
          )
          .required(
            Labels.getLabel(
              "assignPaycard.validation.cardNumberRequired",
              "Card 4 last digits are required"
            )
          ),
      };
    }
    setValidationSchema(yup.object(finalValidationSchema));
  };

  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const loaded = useRef(false);

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Query for preauthorized member lookup
  const {
    data: preauthorizedMemberData,
    isFetching: isFetchingPreauthorizedMember,
    isError: isErrorPreauthorizedMember,
  } = useQuery(
    ["preauthorizedMember", memberIdToLookup],
    () => fetchPreauthorizedMember(memberIdToLookup),
    {
      enabled: !!memberIdToLookup && memberIdToLookup.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const formik = useFormik({
    initialValues: {
      cardNumber: dataPaycardDetail?.cardNumber,
      email: "",
      employeeNo: "",
      firstName: "",
      middle: "",
      lastName: "",
      mobilePhone: "",
      dob: "",
      ssn: "",
      idLanguage: selectedLanguage,
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      askForDenomination: false,
      amount: "",
      additionalAccountManagerFirstName: "",
      additionalAccountManagerLastName: "",
      additionalAccountManagerDob: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      assignPaycardMutation.mutate({ ...values });
    },
  });

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  const handleClearForm = () => {
    // Reset formik form to initial values
    formik.resetForm();

    // Clear all state variables
    setAddressLine1("");
    setAddressLine2("");
    setCity("");
    setState("");
    setZipCode("");
    setSsn("");
    setMemberCode("");
    setMobilePhone("");
    setDateOfBirth("");
    setMemberIdToLookup("");
    if (!hideLanguage) {
      setSelectedLanguage("en");
    }

    // Clear Google Places autocomplete
    setValue(null);
    setInputValue("");
  };

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${window.GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 400),
    []
  );

  const fetchPlaceDetail = useMemo(
    () => (request, callback) => {
      if (request && request.place_id) {
        const getInfo = (address_components, key) => {
          for (let i = 0; i < address_components.length; i++) {
            if (address_components[i].types.includes(key))
              return address_components[i].short_name
                ? address_components[i].short_name
                : "";
          }
          return "";
        };

        const service = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );
        service.getDetails({ placeId: request.place_id }, syncWithForm);

        function syncWithForm(place, status) {
          setFormikSynced(
            "addressLine1",
            getInfo(place.address_components, "street_number") +
              " " +
              getInfo(place.address_components, "route"),
            setAddressLine1
          );
          setFormikSynced(
            "addressLine2",
            getInfo(place.address_components, "subpremise"),
            setAddressLine2
          );
          setFormikSynced(
            "city",
            getInfo(place.address_components, "locality"),
            setCity
          );
          setFormikSynced(
            "state",
            getInfo(place.address_components, "administrative_area_level_1"),
            setState
          );
          setFormikSynced(
            "zipCode",
            getInfo(place.address_components, "postal_code"),
            setZipCode
          );
        }
      }
    },
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: {
          country: ["us"],
        },
        fields: ["description", "place_id"],
      },
      (results) => {
        if (active) {
          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  useEffect(() => {
    fetchPlaceDetail(value);
  }, [value, fetchPlaceDetail]);

  // Auto-populate form when preauthorized member is found
  useEffect(() => {
    if (preauthorizedMemberData?.data && !isErrorPreauthorizedMember) {
      const memberData = preauthorizedMemberData.data;

      // The backend now returns decrypted fields, so we can use them directly
      // Always set all fields to ensure previous values are cleared

      // Personal information fields
      formik.setFieldValue("firstName", memberData.firstName || "");
      formik.setFieldValue("lastName", memberData.lastName || "");
      formik.setFieldValue("email", memberData.email || "");
      formik.setFieldValue("mobilePhone", memberData.phone || "");
      formik.setFieldValue("dob", memberData.dob || "");

      // Address fields
      setFormikSynced(
        "addressLine1",
        memberData.addressLine1 || "",
        setAddressLine1
      );
      setFormikSynced(
        "addressLine2",
        memberData.addressLine2 || "",
        setAddressLine2
      );
      setFormikSynced("city", memberData.city || "", setCity);
      setFormikSynced("state", memberData.state || "", setState);
      setFormikSynced("zipCode", memberData.zip || "", setZipCode);
    }
  }, [preauthorizedMemberData, isErrorPreauthorizedMember]);

  const setFormikSynced = (key, value, callback) => {
    formik.setFieldValue(key, value, false);
    callback(value);
  };

  const wrapperFormikHandleChanged = (event) => {
    formik.handleChange(event);
    let callback = () => {};
    if (event.target.id === "addressLine1") callback = setAddressLine1;
    if (event.target.id === "addressLine2") callback = setAddressLine2;
    if (event.target.id === "city") callback = setCity;
    if (event.target.id === "state") callback = setState;
    if (event.target.id === "zipCode") callback = setZipCode;
    setFormikSynced(event.target.id, event.target.value, callback);
  };

  const handleMemberCodeChange = (event) => {
    formik.handleChange(event);
  };

  const handleMemberCodeBlur = (event) => {
    const memberId = event.target.value.trim();
    if (memberId && memberId.length > 0) {
      setMemberIdToLookup(memberId);
    }
  };

  const setupFormWithServerData = (dataPaycardDetail) => {
    formik.resetForm();
    setHideSsn(false);
    setHideMemberCode(false);
    setHideAddress(false);
    setHidePhone(false);
    setHideDateOfBirth(false);
    setOptionalSsn(false);
    setOptionalMemberCode(false);
    setOptionalPhone(false);
    setOptionalDateOfBirth(false);
    setUseLastFourOfSSN(false);
    setAllowNoEmailCheck(false);
    setHideEmail(false);
    setHideNames(false);
    setAskForDenomination(false);
    setAllowAdditionalAccountManager(null);
    setAdditionalAccountManagerChecked(false);
    setHideLanguage(false);
    setSsn("");
    setMemberCode("");
    setMobilePhone("");
    setDateOfBirth("");
    const { data } = dataPaycardDetail;
    const {
      cardNumber,
      firstName,
      lastName,
      addressLine1: addressLine1FromServer,
      addressLine2: addressLine2FromServer,
      city: cityFromServer,
      state: stateFromServer,
      zipCode: zipCodeFromServer,
      ssn: ssnFromServer,
      employeeNo: employeeNoFromServer,
      mobilePhone: mobilePhoneFromServer,
      dob: dobFromServer,
    } = data.payCardDetail;

    if (dataPaycardDetail !== undefined) {
      if (hasValue(cardNumber)) {
        formik.values.cardNumber = cardNumber;
      }

      if (hasValue(firstName)) {
        formik.values.firstName = firstName;
      }

      if (hasValue(lastName)) {
        formik.values.lastName = lastName;
      }

      if (hasValue(addressLine1FromServer)) {
        setAddressLine1(addressLine1FromServer);
        setFormikSynced(
          "addressLine1",
          addressLine1FromServer,
          setAddressLine1
        );
      }

      if (hasValue(addressLine2FromServer)) {
        setAddressLine2(addressLine2FromServer);
        setFormikSynced(
          "addressLine2",
          addressLine2FromServer,
          setAddressLine2
        );
      }

      if (hasValue(cityFromServer)) {
        setCity(cityFromServer);
        setFormikSynced("city", cityFromServer, setCity);
      }

      if (hasValue(stateFromServer)) {
        setState(stateFromServer);
        setFormikSynced("state", stateFromServer, setState);
      }

      if (hasValue(zipCodeFromServer)) {
        setZipCode(zipCodeFromServer);
        setFormikSynced("zipCode", zipCodeFromServer, setZipCode);
      }

      const {
        hideSSN,
        usePhoneAsMemberId,
        useProxyAsMemberId,
        hideAddress,
        useLastFourOfSSN,
        hidePhone,
        hideDOB,
        optionalSSN,
        optionalPhone,
        optionalDOB,
        optionalMemberCode,
        allowNoEmail,
        hideEmail,
        hideNames,
        askForDenomination,
        allowAdditionalAccountManager,
        hideLanguage,
        defaultLanguage,
      } = data?.payCardAssignProperties || {};

      let finalValidationSchema = { ...validationSchemaObject };

      if (isEnabled(hideEmail)) {
        setHideEmail(true);
        formik.values.email = data?.payCardDetail?.email || "";
      }

      if (isEnabled(hideNames)) {
        setHideNames(true);
        formik.values.firstName = data?.payCardDetail?.firstName || "";
        formik.values.lastName = data?.payCardDetail?.lastName || "";
        formik.values.middle = data?.payCardDetail?.middle || "";
      }

      if (isEnabled(hideSSN)) {
        setHideSsn(true);
        setSsn(ssnFromServer);
      } else if (isEnabled(useLastFourOfSSN)) {
        setUseLastFourOfSSN(true);
        const ssnValidation = yup
          .string()
          .matches(
            /^(\d{4})*$/,
            Labels.getLabel(
              "assignPaycard.validation.lastFourSSN",
              "Must be the last four of the SSN"
            )
          )
          .required(
            Labels.getLabel(
              "assignPaycard.validation.ssnRequired",
              "SSN is required"
            )
          );
        finalValidationSchema = {
          ...finalValidationSchema,
          ssn: ssnValidation,
        };
      } else if (isEnabled(optionalSSN)) {
        setOptionalSsn(true);
        const ssnValidation = yup
          .string()
          .matches(
            /^((?!666|000)[0-8][0-9_]{2}(-)?(?!00)[0-9_]{2}(-)?(?!0000)[0-9_]{4})|(9\d{2})([ -]?)(5\d|6[0-5]|7\d|8[0-8]|9[0-2,4-9])([ -]?)(\d{4})*$/,
            Labels.getLabel(
              "assignPaycard.validation.validSSN",
              "Must be a valid SSN or ITIN"
            ),
            {
              excludeEmptyString: true,
            }
          )
          .notRequired();
        finalValidationSchema = {
          ...finalValidationSchema,
          ssn: ssnValidation,
        };
      } else {
        const ssnValidation = yup
          .string()
          .matches(
            /^((?!666|000)[0-8][0-9_]{2}(-)?(?!00)[0-9_]{2}(-)?(?!0000)[0-9_]{4})|(9\d{2})([ -]?)(5\d|6[0-5]|7\d|8[0-8]|9[0-2,4-9])([ -]?)(\d{4})*$/,
            Labels.getLabel(
              "assignPaycard.validation.validSSN",
              "Must be a valid SSN or ITIN"
            )
          )
          .required(
            Labels.getLabel(
              "assignPaycard.validation.ssnRequired",
              "SSN is required"
            )
          );
        finalValidationSchema = {
          ...finalValidationSchema,
          ssn: ssnValidation,
        };
      }

      if (isEnabled(hidePhone)) {
        setHidePhone(true);
        setMobilePhone(mobilePhoneFromServer);
      } else if (isEnabled(optionalPhone)) {
        setOptionalPhone(true);
        const phoneValidation = yup
          .string(
            Labels.getLabel(
              "assignPaycard.validation.enterPhone",
              "Enter the member mobile phone"
            )
          )
          .test(
            "valid-phone-format",
            Labels.getLabel(
              "assignPaycard.validation.validPhone",
              "Enter a valid phone number"
            ),
            (value) => !value || isValidPhoneNumber(value)
          )
          .notRequired();
        finalValidationSchema = {
          ...finalValidationSchema,
          mobilePhone: phoneValidation,
        };
      } else {
        const phoneValidation = yup
          .string(
            Labels.getLabel(
              "assignPaycard.validation.enterPhone",
              "Enter the member mobile phone"
            )
          )
          .test(
            "valid-phone-format",
            Labels.getLabel(
              "assignPaycard.validation.validPhone",
              "Enter a valid phone number"
            ),
            (value) => isValidPhoneNumber(value)
          )
          .required(
            Labels.getLabel(
              "assignPaycard.validation.phoneRequired",
              "Mobile phone is required"
            )
          );
        finalValidationSchema = {
          ...finalValidationSchema,
          mobilePhone: phoneValidation,
        };
      }

      if (isEnabled(hideDOB)) {
        setHideDateOfBirth(true);
        setDateOfBirth(dobFromServer);
      } else if (isEnabled(optionalDOB)) {
        setOptionalDateOfBirth(true);
        const dobValidation = yup
          .date()
          .transform((value, originalValue) =>
            originalValue === "" ? null : value
          )
          .nullable()
          .notRequired();
        finalValidationSchema = {
          ...finalValidationSchema,
          dob: dobValidation,
        };
      } else {
        const dobValidation = yup
          .date()
          .required(
            Labels.getLabel(
              "assignPaycard.validation.birthdayRequired",
              "Birthday is required"
            )
          );
        finalValidationSchema = {
          ...finalValidationSchema,
          dob: dobValidation,
        };
      }

      if (isEnabled(hideAddress)) {
        setHideAddress(true);
      }

      if (isEnabled(useProxyAsMemberId)) {
        setMemberCode(employeeNoFromServer);
      }

      if (isEnabled(useProxyAsMemberId) || isEnabled(usePhoneAsMemberId)) {
        setHideMemberCode(true);
      } else if (isEnabled(optionalMemberCode)) {
        setOptionalMemberCode(true);
        finalValidationSchema = {
          ...finalValidationSchema,
          employeeNo: yup
            .string(
              Labels.getLabel(
                "assignPaycard.validation.enterEmployeeCode",
                "Enter the employee code"
              )
            )
            .notRequired(),
        };
      } else {
        const employeeValidation = yup
          .string(
            Labels.getLabel(
              "assignPaycard.validation.enterEmployeeCode",
              "Enter the employee code"
            )
          )
          .required(
            Labels.getLabel(
              "assignPaycard.validation.employeeCodeRequired",
              "Employee code is required"
            )
          );
        finalValidationSchema = {
          ...finalValidationSchema,
          employeeNo: employeeValidation,
        };
      }
      setValidationSchema(yup.object(finalValidationSchema));

      if (hasValue(allowNoEmail) && allowNoEmail === "true") {
        setAllowNoEmailCheck(true);
      }

      if (hasValue(askForDenomination) && askForDenomination === "true") {
        setAskForDenomination(true);
        formik.setFieldValue("askForDenomination", true);
      }

      if (hasValue(allowAdditionalAccountManager)) {
        setAllowAdditionalAccountManager(allowAdditionalAccountManager);
      } else {
        setAllowAdditionalAccountManager(null);
      }

      if (isEnabled(hideLanguage)) {
        setHideLanguage(true);
        const lang = hasValue(defaultLanguage) ? defaultLanguage : "en";
        setSelectedLanguage(lang);
        formik.values.idLanguage = lang;
      }
    }
  };

  const renderAddressInfo = () => {
    return (
      <div style={{ display: hideAddress ? "none" : "block" }}>
        <h4>
          {Labels.getLabel(
            "assignPaycard.AddressInformation",
            "Address information"
          )}
        </h4>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              id="google-control"
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.description
              }
              filterOptions={(x) => x}
              options={options}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={value}
              onChange={(event, newValue) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={Labels.getLabel(
                    "assignPaycard.AddLocation",
                    "Add a location"
                  )}
                  fullWidth
                />
              )}
              renderOption={(props, option) => {
                const matches =
                  option.structured_formatting.main_text_matched_substrings;
                const parts = parse(
                  option.structured_formatting.main_text,
                  matches.map((match) => [
                    match.offset,
                    match.offset + match.length,
                  ])
                );

                return (
                  <li {...props}>
                    <Grid container alignItems="center">
                      <Grid item>
                        <Box
                          component={LocationOnIcon}
                          sx={{ color: "text.secondary", mr: 2 }}
                        />
                      </Grid>
                      <Grid item xs>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              fontWeight: part.highlight ? 700 : 400,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}

                        <Typography variant="body2" color="text.secondary">
                          {option.structured_formatting.secondary_text}
                        </Typography>
                      </Grid>
                    </Grid>
                    <div id="map"></div>
                  </li>
                );
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              fullWidth
              id="addressLine1"
              label={Labels.getLabel(
                "assignPaycard.AddressLine1",
                "Address line 1"
              )}
              value={addressLine1}
              onChange={wrapperFormikHandleChanged}
              error={
                formik.touched.addressLine1 &&
                Boolean(formik.errors.addressLine1)
              }
              helperText={
                formik.touched.addressLine1 && formik.errors.addressLine1
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              fullWidth
              id="addressLine2"
              label={Labels.getLabel(
                "assignPaycard.AddressLine2",
                "Address line 2"
              )}
              value={addressLine2}
              onChange={wrapperFormikHandleChanged}
              error={
                formik.touched.addressLine2 &&
                Boolean(formik.errors.addressLine2)
              }
              helperText={
                formik.touched.addressLine2 && formik.errors.addressLine2
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              size="small"
              fullWidth
              id="city"
              label={Labels.getLabel("assignPaycard.City", "City")}
              value={city}
              onChange={wrapperFormikHandleChanged}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              size="small"
              fullWidth
              id="state"
              label={Labels.getLabel("assignPaycard.State", "State")}
              value={state}
              onChange={wrapperFormikHandleChanged}
              error={formik.touched.state && Boolean(formik.errors.state)}
              helperText={formik.touched.state && formik.errors.state}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              size="small"
              fullWidth
              id="zipCode"
              label={Labels.getLabel("assignPaycard.ZipCode", "Zip Code")}
              value={zipCode}
              onChange={wrapperFormikHandleChanged}
              error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
              helperText={formik.touched.zipCode && formik.errors.zipCode}
            />
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <Fragment>
      <br />
      <Container maxWidth="lg">
        {isFetchingPayCardDetail || isFetchingDataPayCardAsReplacement ? (
          <CircularProgress color="inherit" />
        ) : isErrorPaycardDetail || isErrorDataPayCardAsReplacement ? (
          <h4>
            {Labels.getLabel(
              "assignPaycard.ErrorGettingInfo",
              "There was an error getting the info"
            )}
          </h4>
        ) : (
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <br />
              <br />
              <br />
              <img src={paycardImg} alt="paycard" width="60%" loading="lazy" />
              {/* <img decoding="async" width="600" height="600" alt="PayCard mockup transparent background" data-srcset="https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background.png?lossy=1&amp;strip=1&amp;webp=1 1000w, https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background-300x300.png?lossy=1&amp;strip=1&amp;webp=1 300w, https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background-150x150.png?lossy=1&amp;strip=1&amp;webp=1 150w, https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background-768x768.png?lossy=1&amp;strip=1&amp;webp=1 768w" data-src="https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background.png?lossy=1&amp;strip=1&amp;webp=1" data-sizes="(max-width: 1000px) 100vw, 1000px" class="attachment-full size-full wp-image-9114 lazyloaded" src="https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background.png?lossy=1&amp;strip=1&amp;webp=1" sizes="(max-width: 1000px) 100vw, 1000px" srcset="https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background.png?lossy=1&amp;strip=1&amp;webp=1 1000w, https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background-300x300.png?lossy=1&amp;strip=1&amp;webp=1 300w, https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background-150x150.png?lossy=1&amp;strip=1&amp;webp=1 150w, https://b3212019.smushcdn.com/3212019/wp-content/uploads/2023/06/PayCard-mockup-transparent-background-768x768.png?lossy=1&amp;strip=1&amp;webp=1 768w"></img>*/}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <h1>{Labels.getLabel("assignPaycard.Title", "Assign card")}</h1>
                <div
                  style={{
                    display:
                      dataPayCardAsReplacement?.data?.value === "true" ||
                      onTheFly
                        ? "none"
                        : "initial",
                  }}
                >
                  <h4>
                    {Labels.getLabel(
                      "assignPaycard.CardInformation",
                      "Card information"
                    )}
                  </h4>
                  <TextField
                    size="small"
                    fullWidth
                    id="cardNumber"
                    label={Labels.getLabel(
                      "assignPaycard.CardLast4Numbers",
                      "Card last 4 numbers"
                    )}
                    variant="outlined"
                    required
                    disabled
                    value={formik.values.cardNumber}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.cardNumber &&
                      Boolean(formik.errors.cardNumber)
                    }
                    helperText={
                      formik.touched.cardNumber && formik.errors.cardNumber
                    }
                  />
                </div>
                <h4>
                  {Labels.getLabel(
                    "assignPaycard.MemberInformation",
                    "Member/Worker information"
                  )}
                </h4>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Box
                      component="span"
                      sx={{
                        color: "secondary",
                        p: 1,
                        border: "1px dashed grey",
                      }}
                      style={{ display: noEmailChecked ? "block" : "none" }}
                    >
                      <FmdBadIcon color="secondary" />
                      <Typography fontSize={14}>
                        {Labels.getLabel(
                          "assignPaycard.NoEmailWarning",
                          "The members without email won't have access to EWA, Send Money nor Bill Pay"
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={5}
                    sx={{ display: hideEmail ? "none" : "block" }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      id="email"
                      type="email"
                      label={
                        noEmailChecked === true
                          ? Labels.getLabel(
                              "assignPaycard.MemberHasNoEmail",
                              "Member has no email"
                            )
                          : Labels.getLabel(
                              "assignPaycard.VerifiedEmail",
                              "Verified email"
                            )
                      }
                      variant="outlined"
                      required={!noEmailChecked}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      disabled={noEmailChecked}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>
                  <Grid
                    style={{
                      display:
                        allowNoEmailCheck && !hideEmail ? "block" : "none",
                    }}
                    item
                    xs={3}
                  >
                    <FormControlLabel
                      onChange={handleNoEmailCheckChange}
                      control={<Checkbox />}
                      label={Labels.getLabel(
                        "assignPaycard.NoEmail",
                        "No email"
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      style={{ display: hideMemberCode ? "none" : "block" }}
                      size="small"
                      fullWidth
                      id="employeeNo"
                      type="text"
                      label={Labels.getLabel(
                        "assignPaycard.MemberCode",
                        "Member code"
                      )}
                      variant="outlined"
                      required={!optionalMemberCode}
                      value={formik.values.employeeNo}
                      onChange={handleMemberCodeChange}
                      onBlur={handleMemberCodeBlur}
                      error={
                        formik.touched.employeeNo &&
                        Boolean(formik.errors.employeeNo)
                      }
                      helperText={
                        formik.touched.employeeNo && formik.errors.employeeNo
                      }
                    />
                  </Grid>

                  <Grid item xs={5}>
                    <TextField
                      sx={{ display: hideNames ? "none" : "block" }}
                      size="small"
                      fullWidth
                      id="firstName"
                      type="text"
                      label={Labels.getLabel(
                        "assignPaycard.FirstName",
                        "First Name"
                      )}
                      variant="outlined"
                      required
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.firstName &&
                        Boolean(formik.errors.firstName)
                      }
                      helperText={
                        formik.touched.firstName && formik.errors.firstName
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      sx={{ display: hideNames ? "none" : "block" }}
                      size="small"
                      fullWidth
                      id="middle"
                      type="text"
                      label={Labels.getLabel(
                        "assignPaycard.MiddleInitial",
                        "Middle Initial"
                      )}
                      variant="outlined"
                      value={formik.values.middle}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.middle && Boolean(formik.errors.middle)
                      }
                      helperText={formik.touched.middle && formik.errors.middle}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      sx={{ display: hideNames ? "none" : "block" }}
                      size="small"
                      fullWidth
                      id="lastName"
                      type="text"
                      label={Labels.getLabel(
                        "assignPaycard.LastName",
                        "Last Name"
                      )}
                      variant="outlined"
                      required
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.lastName &&
                        Boolean(formik.errors.lastName)
                      }
                      helperText={
                        formik.touched.lastName && formik.errors.lastName
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    style={{ display: hidePhone ? "none" : "block" }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      id="mobilePhone"
                      type="text"
                      label={Labels.getLabel(
                        "assignPaycard.MobilePhone",
                        "Mobile Phone"
                      )}
                      variant="outlined"
                      required={!optionalPhone}
                      value={formik.values.mobilePhone}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.mobilePhone &&
                        Boolean(formik.errors.mobilePhone)
                      }
                      helperText={
                        formik.touched.mobilePhone && formik.errors.mobilePhone
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    style={{ display: hideDateOfBirth ? "none" : "block" }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      id="dob"
                      type="date"
                      label={Labels.getLabel("assignPaycard.DOB", "DOB")}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      required={!optionalDateOfBirth}
                      value={formik.values.dob}
                      onChange={formik.handleChange}
                      error={formik.touched.dob && Boolean(formik.errors.dob)}
                      helperText={formik.touched.dob && formik.errors.dob}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      style={{ display: hideSsn ? "none" : "block" }}
                      size="small"
                      fullWidth
                      id="ssn"
                      type="text"
                      label={Labels.getLabel(
                        "assignPaycard.SSN",
                        "SSN or ITIN"
                      )}
                      variant="outlined"
                      required={!optionalSsn}
                      value={formik.values.ssn}
                      onChange={formik.handleChange}
                      error={formik.touched.ssn && Boolean(formik.errors.ssn)}
                      helperText={formik.touched.ssn && formik.errors.ssn}
                    />
                  </Grid>
                </Grid>
                {allowAdditionalAccountManager && (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        onChange={handleAdditionalAccountManagerCheckChange}
                        control={<Checkbox />}
                        label={Labels.getLabel(
                          "assignPaycard.AdditionalAccountManager",
                          "Needs an additional account manager" +
                            " (" +
                            allowAdditionalAccountManager +
                            ") ?"
                        )}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        sx={{
                          display: !additionalAccountManagerChecked
                            ? "none"
                            : "block",
                        }}
                        size="small"
                        fullWidth
                        id="additionalAccountManagerFirstName"
                        type="text"
                        label={
                          allowAdditionalAccountManager +
                          " " +
                          Labels.getLabel(
                            "assignPaycard.FirstName",
                            "First Name"
                          )
                        }
                        variant="outlined"
                        required
                        value={formik.values.additionalAccountManagerFirstName}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.additionalAccountManagerFirstName &&
                          Boolean(
                            formik.errors.additionalAccountManagerFirstName
                          )
                        }
                        helperText={
                          formik.touched.additionalAccountManagerFirstName &&
                          formik.errors.additionalAccountManagerFirstName
                        }
                      />
                    </Grid>

                    <Grid item xs={5}>
                      <TextField
                        sx={{
                          display: !additionalAccountManagerChecked
                            ? "none"
                            : "block",
                        }}
                        size="small"
                        fullWidth
                        id="additionalAccountManagerLastName"
                        type="text"
                        label={
                          allowAdditionalAccountManager +
                          " " +
                          Labels.getLabel("assignPaycard.LastName", "Last Name")
                        }
                        variant="outlined"
                        required
                        value={formik.values.additionalAccountManagerLastName}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.additionalAccountManagerLastName &&
                          Boolean(
                            formik.errors.additionalAccountManagerLastName
                          )
                        }
                        helperText={
                          formik.touched.additionalAccountManagerLastName &&
                          formik.errors.additionalAccountManagerLastName
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      style={{
                        display: !additionalAccountManagerChecked
                          ? "none"
                          : "block",
                      }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        id="additionalAccountManagerDob"
                        type="date"
                        label={
                          allowAdditionalAccountManager +
                          " " +
                          Labels.getLabel("assignPaycard.DOB", " DOB")
                        }
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={formik.values.additionalAccountManagerDob}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.additionalAccountManagerDob &&
                          Boolean(formik.errors.additionalAccountManagerDob)
                        }
                        helperText={
                          formik.touched.additionalAccountManagerDob &&
                          formik.errors.additionalAccountManagerDob
                        }
                      />
                    </Grid>
                  </Grid>
                )}
                <br />
                <Grid item xs={10}>
                  <FormControl
                    sx={{
                      display: hideEmail || hideLanguage ? "none" : "block",
                    }}
                    error={
                      formik.touched.idLocation &&
                      Boolean(formik.errors.idLocation)
                    }
                  >
                    <InputLabel id="idLanguage">
                      {Labels.getLabel("assignPaycard.Language", "Language")}
                    </InputLabel>
                    <Select
                      style={{ height: "40px", width: "200px" }}
                      labelId="idLanguage"
                      id="simple-select"
                      label="Language"
                      onChange={handleLanguageChange}
                      value={formik.values.idLanguage}
                    >
                      <MenuItem key="en" value="en">
                        {Labels.getLabel("assignPaycard.English", "English")}
                      </MenuItem>
                      <MenuItem key="es" value="es">
                        {Labels.getLabel("assignPaycard.Spanish", "Spanish")}
                      </MenuItem>
                    </Select>
                    <FormHelperText>
                      {formik.touched.idLanguage && formik.errors.idLanguage}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={8}
                  sx={{ display: askForDenomination ? "block" : "none" }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    id="amount"
                    label={Labels.getLabel(
                      "assignPaycard.LoadValueLabel",
                      "Please enter the load value for the card"
                    )}
                    type="number"
                    inputProps={{ min: 1, max: 1000, step: 1 }}
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    onInput={(e) => {
                      // If value is negative, set it to 0.1
                      if (e.target.value < 1) {
                        e.target.value = "1";
                        formik.setFieldValue("amount", "1");
                      }
                      if (e.target.value > 1000) {
                        e.target.value = 1000;
                        formik.setFieldValue("amount", 1000);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent typing the minus key
                      if (e.key === "-") {
                        e.preventDefault();
                      }
                    }}
                    error={
                      formik.touched.amount && Boolean(formik.errors.amount)
                    }
                    helperText={formik.touched.amount && formik.errors.amount}
                  />
                </Grid>
                {renderAddressInfo()}
                <br />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleClearForm}
                      disabled={assignPaycardMutation.isLoading}
                    >
                      {Labels.getLabel("assignPaycard.Clear", "Clear Form")}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit();
                      }}
                      disabled={
                        assignPaycardMutation.isLoading || feedback.open
                      }
                    >
                      {!assignPaycardMutation.isLoading
                        ? Labels.getLabel("assignPaycard.Assign", "Assign")
                        : Labels.getLabel(
                            "assignPaycard.Processing",
                            "Processing..."
                          )}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        )}
        <Snackbar
          open={feedback.open}
          autoHideDuration={6000}
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
            severity={feedback.type}
            sx={{ width: "100%" }}
          >
            {feedback.message}
          </Alert>
        </Snackbar>
      </Container>
    </Fragment>
  );
}
